import userResources from "api/userResources";
import InputField from "components/fields/InputField";
import getSelectClassNames from "components/SelectFieldClasses/SelectFieldClasses";
import React, { useEffect, useState } from "react";
import { FaPhone } from "react-icons/fa";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import Select from "react-tailwindcss-select";
import { toast } from "react-toastify";

export default function CustomerDetails({
  register,
  errors,
  clearErrors,
  watch,
  setValue,
  setError,
  control,
  getValues,
}) {
  // If misc is true, use misc.customer_details, otherwise use airticket.customer_details.
  const customerPath = "customer_details";

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [customerData, setCustomerData] = useState([]);
  const [counties, setCounties] = useState([]);
  const [state, setState] = useState([]);
  const [city, setCity] = useState([]);

  const conturySelectedValue = getValues(`${customerPath}.country_id`);
  const stateSelectedValue = getValues(`${customerPath}.state_id`);

  const getCountries = async () => {
    try {
      const response = await userResources.countries();
      const combinedData = response.data.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      setCounties(combinedData);
    } catch (error) {
      console.log(error);
    }
  };

  const getState = async (countryValue) => {
    try {
      const response = await userResources.state(countryValue);
      const combinedData = response.data.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      setState(combinedData);
    } catch (error) {
      console.log(error);
    }
  };

  const getCity = async (stateValue) => {
    try {
      const response = await userResources.city(stateValue);
      const combinedData = response.data.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      setCity(combinedData);
    } catch (error) {
      console.log(error);
    }
  };

  const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleFindProfile = async (rawEmail) => {
    // Normalize the email input.
    const email = typeof rawEmail === "string" ? rawEmail.trim() : "";

    // Validate email presence.
    if (!email) {
      return toast.error("Please enter a customer email ID for searching.");
    }

    // Validate email format.
    if (!EMAIL_PATTERN.test(email)) {
      return toast.error("Please enter a valid email ID!");
    }

    // Show a loading toast and store its ID so we can update or dismiss it later.
    const loadingToastId = toast.loading("Fetching customer data...", {
      autoClose: false,
    });

    setIsLoading(true);
    try {
      // Call the customer API.
      const response = await userResources.customerList({ email });
      // Safely extract the customer data.
      const customerData = response?.data?.data;

      // If the data is an array and not empty, update the state.
      if (Array.isArray(customerData) && customerData.length > 0) {
        setCustomerData(customerData);
        toast.update(loadingToastId, {
          render: "Customer data loaded successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        console.warn(
          "No customer data found or unexpected response structure:",
          response
        );
        setCustomerData([]);
        toast.update(loadingToastId, {
          render: "No customer data found for the given email.",
          type: "info",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching customer data:", error.response || error);
      toast.update(loadingToastId, {
        render: "Failed to fetch customer data. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (customerData.length > 0) {
      const updateField = (field, value) => {
        if (value) {
          setValue(field, value);
          if (getValues(field)) {
            clearErrors(field);
          }
        }
      };
      updateField("customer_id", customerData[0]?.id);
      updateField(`${customerPath}.full_name`, customerData[0]?.full_name);
      updateField(`${customerPath}.email`, customerData[0]?.email);
      updateField(
        `${customerPath}.phone_number`,
        customerData[0]?.phone_number
      );
      updateField(
        `${customerPath}.alternate_phone`,
        customerData[0]?.alternate_phone
      );
      updateField(`${customerPath}.address`, customerData[0]?.address);
      updateField(
        `${customerPath}.country_id`,
        customerData[0]?.countryName?.id
      );
      updateField(`${customerPath}.state_id`, customerData[0]?.stateName?.id);
      updateField(`${customerPath}.city_id`, customerData[0]?.cityName?.id);
      updateField(`${customerPath}.postal_code`, customerData[0]?.postal_code);
      updateField(
        `${customerPath}.passport_number`,
        customerData[0]?.passport_number
      );
    }
  }, [setValue, clearErrors, getValues, customerData, customerPath]);

  const clearAllFields = () => {
    const keys = [
      `${customerPath}.full_name`,
      `${customerPath}.email`,
      `${customerPath}.phone_number`,
      `${customerPath}.alternate_phone`,
      `${customerPath}.address`,
      `${customerPath}.country_id`,
      `${customerPath}.state_id`,
      `${customerPath}.city_id`,
      `${customerPath}.postal_code`,
      `${customerPath}.passport_number`,
    ];

    keys.forEach((key) => {
      setValue(key, ""); // Clear the field
      clearErrors(key); // Remove validation error
    });

    setEmail(""); // Clear email state if used separately
    setCustomerData([]); // Reset customer data
  };

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    setState([]);
    setCity([]);
    if (conturySelectedValue) {
      getState(conturySelectedValue);
    }
  }, [conturySelectedValue]);

  useEffect(() => {
    setCity([]);
    if (conturySelectedValue && stateSelectedValue) {
      getCity({ country: conturySelectedValue, state: stateSelectedValue });
    }
  }, [stateSelectedValue, conturySelectedValue]);

  return (
    <section className="mt-2 rounded-md bg-gray-900 p-2">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-[#ff4e41] p-2 text-white">
        <div>
          <label className="md:whitespace-nowrap">Customer Details:</label>
        </div>
        <div className="flex-grow">
          <input
            type="email"
            name="search"
            id="search"
            className="w-full rounded-sm bg-white px-2 text-gray-900"
            placeholder="Enter customer mail ID to search"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault(); // Prevent form submission
                handleFindProfile(email); // Call the function
              }
            }}
          />
        </div>
        <button
          onClick={() => handleFindProfile(email)}
          className={`flex items-center justify-center gap-0.5 rounded-sm px-2 text-gray-900 ${
            isLoading
              ? "cursor-not-allowed"
              : " text-green-500 hover:bg-green-500 hover:text-white"
          } bg-white`}
          type="button"
          disabled={isLoading}
        >
          Find Profile
          {isLoading && (
            <span className="loading loading-dots loading-xs text-blue-500"></span>
          )}
        </button>

        <button
          onClick={clearAllFields}
          className="rounded-sm bg-white px-2 text-red-500 hover:bg-red-500 hover:text-white"
          type="button"
        >
          Clear
        </button>
      </div>
      <div
        className={`grid grid-cols-1 items-end gap-x-2 sm:grid-cols-2 md:grid-cols-3 `}
      >
        <div>
          <InputField
            label="Full Name"
            id="full_name"
            name={`${customerPath}.full_name`}
            placeholder="Enter full name"
            register={register}
            rules={{
              required: "Full Name is required",
            }}
            errorMessage={
              errors &&
              errors[customerPath.split(".")[0]] &&
              errors[customerPath.split(".")[0]].customer_details &&
              errors[customerPath.split(".")[0]].customer_details.full_name &&
              errors[customerPath.split(".")[0]].customer_details.full_name
                .message
            }
            bgAllDark={true}
            onChange={() => clearErrors(`${customerPath}.full_name`)}
          />
        </div>

        <div>
          <InputField
            label="Email Address"
            id="email"
            name={`${customerPath}.email`}
            placeholder="Enter email address"
            register={register}
            rules={{
              required: "Email is required",
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: "Invalid email address",
              },
            }}
            errorMessage={
              errors &&
              errors[customerPath.split(".")[0]] &&
              errors[customerPath.split(".")[0]].customer_details &&
              errors[customerPath.split(".")[0]].customer_details.email &&
              errors[customerPath.split(".")[0]].customer_details.email.message
            }
            bgAllDark={true}
            onChange={() => clearErrors(`${customerPath}.email`)}
          />
        </div>

        <div>
          <label
            htmlFor="phone"
            className="my-2 block text-sm font-medium text-[#ff4e41]"
          >
            Phone Number
          </label>
          <div className="relative">
            <div className="flex">
              <PhoneInputWithCountrySelect
                defaultCountry="CA"
                value={getValues(`${customerPath}.phone_number`)}
                onChange={(value) => {
                  setValue(`${customerPath}.phone_number`, value, {
                    shouldValidate: true,
                  });
                  clearErrors(`${customerPath}.phone_number`);
                }}
                placeholder="Enter Phone Number"
                className="h-10 w-full rounded-md border bg-white px-2 text-lg focus:outline-none focus:ring-2 dark:!border-white/10 dark:bg-transparent"
              />
            </div>
            <div className="absolute right-4 top-4">
              <FaPhone />
            </div>
          </div>
          {errors &&
            errors[customerPath.split(".")[0]] &&
            errors[customerPath.split(".")[0]].customer_details &&
            errors[customerPath.split(".")[0]].customer_details
              .phone_number && (
              <span className="text-sm text-red-600">
                {
                  errors[customerPath.split(".")[0]].customer_details
                    .phone_number.message
                }
              </span>
            )}
        </div>

        <div>
          <label
            htmlFor="phone"
            className="my-2 block text-sm font-medium text-[#ff4e41]"
          >
            Alternate Phone Number
          </label>
          <div className="relative">
            <div className="flex">
              <PhoneInputWithCountrySelect
                defaultCountry="CA"
                value={getValues(`${customerPath}.alternate_phone`)}
                onChange={(value) => {
                  setValue(`${customerPath}.alternate_phone`, value);
                  clearErrors(`${customerPath}.alternate_phone`);
                }}
                placeholder="Enter Alternate Phone Number"
                className="h-10 w-full rounded-md border bg-white px-2 text-lg text-white focus:outline-none focus:ring-2 dark:!border-white/10 dark:bg-transparent"
                inputStyle={{ color: "white" }}
              />
            </div>
            <div className="absolute right-4 top-4">
              <FaPhone />
            </div>
          </div>
          {errors &&
            errors[customerPath.split(".")[0]] &&
            errors[customerPath.split(".")[0]].customer_details &&
            errors[customerPath.split(".")[0]].customer_details
              .alternate_phone && (
              <span className="text-sm text-red-600">
                {
                  errors[customerPath.split(".")[0]].customer_details
                    .alternate_phone.message
                }
              </span>
            )}
        </div>

        <div>
          <InputField
            label="Address"
            id="address"
            name={`${customerPath}.address`}
            placeholder="Enter address"
            register={register}
            errorMessage={
              errors &&
              errors[customerPath.split(".")[0]] &&
              errors[customerPath.split(".")[0]].customer_details &&
              errors[customerPath.split(".")[0]].customer_details.address &&
              errors[customerPath.split(".")[0]].customer_details.address
                .message
            }
            bgAllDark={true}
            onChange={() => clearErrors(`${customerPath}.address`)}
          />
        </div>

        <div>
          <label className="my-2 block text-sm font-medium text-[#ff4e41]">
            Country
          </label>
          <Select
            {...register(`${customerPath}.country_id`)}
            value={counties.find(
              (source) => source.value === watch(`${customerPath}.country_id`)
            )}
            onChange={(selectedOption) => {
              setValue(
                `${customerPath}.country_id`,
                selectedOption?.value || ""
              );
              clearErrors(`${customerPath}.country_id`);
            }}
            options={counties}
            isSearchable={true}
            placeholder="Select..."
            classNames={getSelectClassNames({
              isDisabled: false,
              selectProps: {},
              isSelected: false,
              value: watch(`${customerPath}.country_id`),
            })}
            formatOptionLabel={(data) => (
              <li
                className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                  !data.isSelected
                    ? `bg-gray-200 text-gray-900`
                    : `bg-blue-800 text-gray-200`
                }`}
              >
                {data.label}
              </li>
            )}
          />
        </div>

        <div>
          <label className="my-2 block text-sm font-medium text-[#ff4e41]">
            Province/State
          </label>
          <Select
            {...register(`${customerPath}.state_id`)}
            value={state.find(
              (source) => source.value === watch(`${customerPath}.state_id`)
            )}
            onChange={(selectedOption) => {
              setValue(`${customerPath}.state_id`, selectedOption?.value || "");
              clearErrors(`${customerPath}.state_id`);
            }}
            options={state}
            isSearchable={true}
            placeholder="Select..."
            isDisabled={watch(`${customerPath}.country_id`) ? false : true}
            classNames={getSelectClassNames({
              isDisabled: watch(`${customerPath}.country_id`) ? false : true,
              selectProps: {},
              isSelected: false,
              value: watch(`${customerPath}.state_id`),
            })}
            formatOptionLabel={(data) => (
              <li
                className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                  !data.isSelected
                    ? `bg-gray-200 text-gray-900`
                    : `bg-blue-800 text-gray-200`
                }`}
              >
                {data.label}
              </li>
            )}
          />
        </div>

        <div>
          <label className="my-2 block text-sm font-medium text-[#ff4e41]">
            City
          </label>
          <Select
            {...register(`${customerPath}.city_id`)}
            value={city.find(
              (source) => source.value === watch(`${customerPath}.city_id`)
            )}
            onChange={(selectedOption) => {
              setValue(`${customerPath}.city_id`, selectedOption?.value || "");
              clearErrors(`${customerPath}.city_id`);
            }}
            options={city}
            isSearchable={true}
            placeholder="Select..."
            isDisabled={watch(`${customerPath}.state_id`) ? false : true}
            classNames={getSelectClassNames({
              isDisabled: watch(`${customerPath}.state_id`) ? false : true,
              selectProps: {},
              isSelected: false,
              value: watch(`${customerPath}.city_id`),
            })}
            formatOptionLabel={(data) => (
              <li
                className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                  !data.isSelected
                    ? `bg-gray-200 text-gray-900`
                    : `bg-blue-800 text-gray-200`
                }`}
              >
                {data.label}
              </li>
            )}
          />
        </div>

        <div>
          <InputField
            label="Postal/Zip Code"
            id="postal_code"
            name={`${customerPath}.postal_code`}
            placeholder="Enter Postal/Zip Code (optional)"
            register={register}
            bgAllDark={true}
          />
        </div>

        <div>
          <InputField
            label="Passport Number"
            id="passport_number"
            name={`${customerPath}.passport_number`}
            placeholder="Enter passport number (optional)"
            register={register}
            bgAllDark={true}
          />
        </div>
      </div>
    </section>
  );
}
